import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Stack, Typography, Button, TextField, InputAdornment,
  Paper, Table, TableContainer, TableHead, TableRow, TableCell, TableBody,
  IconButton, Tooltip, TablePagination, LinearProgress,
  alpha, useTheme, Menu, ListItemIcon, ListItemText, MenuItem as MenuItemMui,
} from '@mui/material';
import {
  Search, Add, MoreVert, OpenInNew, Clear,
  PlayArrow as OpenIcon, StopCircle as CloseIcon, Archive as ArchiveIcon,
  Link as LinkIcon, FilterAltOff,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { admissionCycleService } from '../../services/admissions/admissionCycleService';
import { AdmissionCycle, AdmissionCycleStatus, Pagination } from '../../types/admissions';
import StatusChip, { statusLabel, ToneChip } from '../../components/common/StatusChip';
import EmptyState from '../../components/common/EmptyState';
import TableRowSkeletons from '../../components/common/TableRowSkeletons';
import { useDebounce } from '../../hooks/useDebounce';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import dateTimeUtils from '../../utils/dateTimeUtils';
import CreateCycleDialog from '../../components/admissions/CreateCycleDialog';
import { CycleAction } from '../../components/admissions/CycleActionsConfirmDialog';
import { useCycleActions } from '../../components/admissions/useCycleActions';
import { intakeHeadline } from '../../components/admissions/intake/intakeCopy';
import SearchableSelect from '../../components/common/SearchableSelect';

const CYCLE_STATUSES: AdmissionCycleStatus[] = ['Draft', 'Open', 'Closed', 'Archived'];

/**
 * Admission Cycles — list + create entry point. Reads filters from the URL
 * so a specific view is shareable. Status, session, and text search are the
 * three filter axes.
 *
 * Row actions (Open / Close / Archive, token mgmt, delete-draft) run through
 * a confirmation dialog so a mis-click can't lose a draft or publish a cycle
 * before it's ready.
 */

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const AdmissionCyclesPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canWrite } = useModulePermissions();
  const canWriteSettings = canWrite('admissions_settings');

  // Filters mirrored to URL for shareable views.
  const urlStatus = searchParams.get('status') ?? '';
  const urlSearch = searchParams.get('q') ?? '';
  const [status, setStatus] = useState<string>(urlStatus);
  const [searchInput, setSearchInput] = useState(urlSearch);
  const search = useDebounce(searchInput, 300);

  const [page, setPage] = useState<number>(() => Math.max(0, (parseInt(searchParams.get('page') ?? '1', 10) || 1) - 1));
  const [rowsPerPage, setRowsPerPage] = useState<number>(() => {
    const parsed = parseInt(searchParams.get('limit') ?? '25', 10);
    return PAGE_SIZE_OPTIONS.includes(parsed) ? parsed : 25;
  });

  const [cycles, setCycles] = useState<AdmissionCycle[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  // Row menu state.
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuTarget, setMenuTarget] = useState<AdmissionCycle | null>(null);

  // Confirm dialog.

  // Keep URL in sync with filters.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (status) next.set('status', status); else next.delete('status');
    if (search.trim()) next.set('q', search.trim()); else next.delete('q');
    if (page > 0) next.set('page', String(page + 1)); else next.delete('page');
    if (rowsPerPage !== 25) next.set('limit', String(rowsPerPage)); else next.delete('limit');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, search, page, rowsPerPage]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await admissionCycleService.list({
        status: status || undefined,
        q: search.trim() || undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      setCycles(res.items);
      setPagination(res.pagination);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to load cycles', { variant: 'error' });
      setCycles([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [status, search, page, rowsPerPage, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);
  const cycleActions = useCycleActions(() => load());
  // Reset to first page on filter change.
  useEffect(() => { setPage(0); }, [status, search]);

  const filtersActive = useMemo(() => !!(status || search.trim()), [status, search]);
  const resetFilters = () => { setStatus(''); setSearchInput(''); setPage(0); };

  const openMenu = (e: React.MouseEvent<HTMLElement>, cycle: AdmissionCycle) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuTarget(cycle);
  };
  const closeMenu = () => { setMenuAnchor(null); setMenuTarget(null); };

  const copyPublicLink = async (cycle: AdmissionCycle) => {
    if (!cycle.publicToken) {
      enqueueSnackbar('Public link not generated yet. Open the cycle to mint one.', { variant: 'info' });
      return;
    }
    const url = admissionCycleService.publicApplyUrl(cycle.publicToken);
    try {
      await navigator.clipboard.writeText(url);
      enqueueSnackbar('Public apply link copied', { variant: 'success' });
    } catch {
      enqueueSnackbar(`Public link: ${url}`, { variant: 'info', persist: true });
    }
    closeMenu();
  };

  const openCycleDetail = (cycle: AdmissionCycle) =>
    navigate(`/app/admissions/cycles/${cycle._id}`);

  const requestAction = (cycle: AdmissionCycle, action: CycleAction) => {
    closeMenu();
    cycleActions.request(cycle, action);
  };

  const scheduleFor = (cycle: AdmissionCycle): string => {
    const open = cycle.schedule?.applicationOpen ? dateTimeUtils.formatDateForDisplay(cycle.schedule.applicationOpen) : '—';
    const close = cycle.schedule?.applicationClose ? dateTimeUtils.formatDateForDisplay(cycle.schedule.applicationClose) : 'no closing date';
    return `${open} → ${close}`;
  };

  return (
    <Box>
      {/* ── Page header ─────────────────────────────────────────────────── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>Admission cycles</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            A cycle is one admission campaign — one session, one form, one fee schedule,
            one merit list.
          </Typography>
        </Box>
        {canWriteSettings && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateOpen(true)}
            disableElevation
          >
            New cycle
          </Button>
        )}
      </Stack>

      {/* ── Filter row ──────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: 2, mb: 3, borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="stretch">
          <TextField
            size="small"
            placeholder="Search cycles by name…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchInput('')}><Clear fontSize="small" /></IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            sx={{ flex: 1, minWidth: 240 }}
          />
          <SearchableSelect
            label="Status"
            value={status}
            onChange={setStatus}
            options={[
              { value: '', label: 'All' },
              ...CYCLE_STATUSES.map(s => ({ value: s, label: statusLabel({ kind: 'admissionCycle', value: s }) })),
            ]}
            placeholder="All statuses"
            minWidth={180}
          />
          {filtersActive && (
            <Button
              size="small"
              variant="text"
              onClick={resetFilters}
              startIcon={<FilterAltOff fontSize="small" />}
            >
              Reset
            </Button>
          )}
        </Stack>
      </Paper>

      {/* ── Table ──────────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        {/* Subtle progress bar during refetch so filter/search feels alive
            without blanking the rows. */}
        {loading && cycles.length > 0 && (
          <LinearProgress sx={{ height: 2 }} />
        )}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.025) }}>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Cycle</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Application window</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Classes begin</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Public link</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && cycles.length === 0 ? (
                <TableRowSkeletons columns={6} rows={5} />
              ) : cycles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 6 }}>
                    <EmptyState
                      icon={<Search />}
                      title={filtersActive ? 'No cycles match these filters' : 'No admission cycles yet'}
                      description={filtersActive
                        ? 'Clear the filters to see all cycles.'
                        : 'Create a cycle to open your first admission campaign.'}
                      action={filtersActive
                        ? { label: 'Reset filters', onClick: resetFilters }
                        : canWriteSettings
                          ? { label: 'New cycle', onClick: () => setCreateOpen(true) }
                          : undefined}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                cycles.map((cycle) => {
                  const sessionLabel = typeof cycle.sessionId === 'object'
                    ? cycle.sessionId.name
                    : undefined;
                  return (
                    <TableRow
                      key={cycle._id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openCycleDetail(cycle)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{cycle.name}</Typography>
                        {sessionLabel && (
                          <Typography variant="caption" color="text.secondary">
                            {sessionLabel}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5} alignItems="flex-start">
                          <StatusChip status={{ kind: 'admissionCycle', value: cycle.status }} />
                          {intakeHeadline(cycle) && <ToneChip label={intakeHeadline(cycle)!.label} tone={intakeHeadline(cycle)!.tone} sx={{ height: 20, fontSize: 10 }} />}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{scheduleFor(cycle)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {cycle.schedule?.classesBeginOn
                            ? dateTimeUtils.formatDateForDisplay(cycle.schedule.classesBeginOn)
                            : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {cycle.status === 'Open' && cycle.publicToken ? (
                          <Tooltip title="Copy public apply link">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); copyPublicLink(cycle); }}
                            >
                              <LinkIcon fontSize="small" color="primary" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Open">
                          <IconButton size="small" onClick={() => openCycleDetail(cycle)}>
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="More">
                          <IconButton size="small" onClick={(e) => openMenu(e, cycle)}>
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {pagination && pagination.totalItems > 0 && (
          <TablePagination
            component="div"
            count={pagination.totalItems}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          />
        )}
      </Paper>

      {/* ── Row menu ───────────────────────────────────────────────────── */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor && menuTarget)}
        onClose={closeMenu}
      >
        {menuTarget && canWriteSettings && menuTarget.status === 'Draft' && (
          <MenuItemMui onClick={() => requestAction(menuTarget, 'open')}>
            <ListItemIcon><OpenIcon fontSize="small" color="success" /></ListItemIcon>
            <ListItemText>Open cycle</ListItemText>
          </MenuItemMui>
        )}
        {menuTarget && canWriteSettings && menuTarget.status === 'Open' && (
          <MenuItemMui onClick={() => requestAction(menuTarget, 'close')}>
            <ListItemIcon><CloseIcon fontSize="small" color="warning" /></ListItemIcon>
            <ListItemText>Close cycle</ListItemText>
          </MenuItemMui>
        )}
        {menuTarget && canWriteSettings && menuTarget.status === 'Closed' && (
          <MenuItemMui onClick={() => requestAction(menuTarget, 'reopen')}>
            <ListItemIcon><OpenIcon fontSize="small" color="success" /></ListItemIcon>
            <ListItemText>Reopen cycle</ListItemText>
          </MenuItemMui>
        )}
        {menuTarget && canWriteSettings && menuTarget.status === 'Closed' && (
          <MenuItemMui onClick={() => requestAction(menuTarget, 'archive')}>
            <ListItemIcon><ArchiveIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Archive cycle</ListItemText>
          </MenuItemMui>
        )}
        {menuTarget && canWriteSettings && menuTarget.status === 'Draft' && (
          <MenuItemMui onClick={() => requestAction(menuTarget, 'delete')}>
            <ListItemIcon><Clear fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Delete draft</ListItemText>
          </MenuItemMui>
        )}
        {menuTarget && menuTarget.status === 'Open' && menuTarget.publicToken && (
          <MenuItemMui onClick={() => copyPublicLink(menuTarget)}>
            <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Copy public link</ListItemText>
          </MenuItemMui>
        )}
      </Menu>

      {/* ── Dialogs ────────────────────────────────────────────────────── */}
      <CreateCycleDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(cycle) => {
          setCreateOpen(false);
          enqueueSnackbar('Cycle created', { variant: 'success' });
          navigate(`/app/admissions/cycles/${cycle._id}`);
        }}
      />

      {cycleActions.dialog}
    </Box>
  );
};

export default AdmissionCyclesPage;
